import { useLanguage } from '../contexts/LanguageContext';
import { QrCode, Zap, Shield, Globe, Users, Star } from 'lucide-react';
import SharedHeader from './SharedHeader';
import Footer from './Footer';

const About = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'About QRCodeForge',
      subtitle: 'Your trusted partner for professional QR code generation',
      sections: {
        mission: {
          title: 'Our Mission',
          content: 'At QRCodeForge, we believe that connecting the physical and digital worlds should be simple, secure, and accessible to everyone. Our mission is to provide the most reliable, user-friendly, and feature-rich QR code generator on the web, empowering individuals and businesses to bridge the gap between offline and online experiences.'
        },
        story: {
          title: 'Our Story',
          content: 'Founded in 2025, QRCodeForge was born from the recognition that existing QR code generators were either too complex, lacked essential features, or compromised user privacy. We set out to create a solution that would be both powerful and simple, secure and accessible. Today, we serve thousands of users worldwide, from small business owners to large enterprises, all united by the need for reliable QR code generation.'
        },
        features: {
          title: 'Why Choose QRCodeForge?',
          items: [
            {
              icon: <Shield className="w-6 h-6" />,
              title: 'Privacy First',
              description: 'All QR code generation happens locally in your browser. We never store or see your data.'
            },
            {
              icon: <Zap className="w-6 h-6" />,
              title: 'Lightning Fast',
              description: 'Generate QR codes instantly with our optimized, client-side processing engine.'
            },
            {
              icon: <Globe className="w-6 h-6" />,
              title: 'Universal Compatibility',
              description: 'Our QR codes work with all major QR code readers and devices worldwide.'
            },
            {
              icon: <Users className="w-6 h-6" />,
              title: 'User Friendly',
              description: 'Intuitive interface designed for both beginners and professionals.'
            },
            {
              icon: <Star className="w-6 h-6" />,
              title: 'Premium Quality',
              description: 'High-resolution output with customizable colors, sizes, and error correction.'
            },
            {
              icon: <QrCode className="w-6 h-6" />,
              title: 'Versatile Types',
              description: 'Support for URLs, text, email, phone, SMS, WiFi, and social media QR codes.'
            }
          ]
        },
        commitment: {
          title: 'Our Commitment',
          content: [
            '• **Free Forever**: Our core QR code generation service will always remain free',
            '• **Continuous Innovation**: We regularly add new features and improve existing ones',
            '• **Privacy Protection**: Your data stays on your device - we never collect or store QR code content',
            '• **Quality Assurance**: Every QR code is generated with the highest standards of quality and compatibility',
            '• **User Support**: We are committed to helping our users succeed with comprehensive documentation and support'
          ]
        },
        technology: {
          title: 'Technology Behind QRCodeForge',
          content: [
            'QRCodeForge is built with modern web technologies to ensure optimal performance and security:',
            '• **Client-Side Processing**: All QR code generation happens in your browser using advanced JavaScript libraries',
            '• **React & TypeScript**: Built with robust, type-safe frontend technologies for reliability',
            '• **Responsive Design**: Optimized for desktop, tablet, and mobile devices',
            '• **Progressive Web App**: Fast loading and offline capabilities for improved user experience',
            '• **International Standards**: Full compliance with ISO/IEC 18004 QR code standards'
          ]
        },
        future: {
          title: 'The Future of QRCodeForge',
          content: 'We are constantly innovating and expanding our platform. Upcoming features include bulk QR code generation, advanced analytics, API access, and enhanced customization options. Our goal is to remain the most comprehensive and user-friendly QR code solution available.'
        }
      }
    },
    zh: {
      title: '关于 QRCodeForge',
      subtitle: '您值得信赖的专业二维码生成合作伙伴',
      sections: {
        mission: {
          title: '我们的使命',
          content: '在 QRCodeForge，我们相信连接物理世界和数字世界应该是简单、安全且人人可及的。我们的使命是提供网络上最可靠、用户友好且功能丰富的二维码生成器，帮助个人和企业弥合线下和线上体验之间的差距。'
        },
        story: {
          title: '我们的故事',
          content: '成立于 2025 年，QRCodeForge 诞生于对现有二维码生成器要么过于复杂、缺乏基本功能，要么损害用户隐私的认识。我们着手创建一个既强大又简单、既安全又易用的解决方案。如今，我们为全球数千用户提供服务，从小企业主到大型企业，都因需要可靠的二维码生成而团结在一起。'
        },
        features: {
          title: '为什么选择 QRCodeForge？',
          items: [
            {
              icon: <Shield className="w-6 h-6" />,
              title: '隐私优先',
              description: '所有二维码生成都在您的浏览器中本地进行。我们从不存储或查看您的数据。'
            },
            {
              icon: <Zap className="w-6 h-6" />,
              title: '闪电般快速',
              description: '使用我们优化的客户端处理引擎即时生成二维码。'
            },
            {
              icon: <Globe className="w-6 h-6" />,
              title: '通用兼容性',
              description: '我们的二维码与全球所有主要二维码阅读器和设备兼容。'
            },
            {
              icon: <Users className="w-6 h-6" />,
              title: '用户友好',
              description: '为初学者和专业人士设计的直观界面。'
            },
            {
              icon: <Star className="w-6 h-6" />,
              title: '优质品质',
              description: '高分辨率输出，可自定义颜色、尺寸和错误纠正。'
            },
            {
              icon: <QrCode className="w-6 h-6" />,
              title: '多样类型',
              description: '支持网址、文本、邮件、电话、短信、WiFi 和社交媒体二维码。'
            }
          ]
        },
        commitment: {
          title: '我们的承诺',
          content: [
            '• **永久免费**：我们的核心二维码生成服务将始终保持免费',
            '• **持续创新**：我们定期添加新功能并改进现有功能',
            '• **隐私保护**：您的数据保存在您的设备上 - 我们从不收集或存储二维码内容',
            '• **质量保证**：每个二维码都以最高质量和兼容性标准生成',
            '• **用户支持**：我们致力于通过全面的文档和支持帮助用户取得成功'
          ]
        },
        technology: {
          title: 'QRCodeForge 背后的技术',
          content: [
            'QRCodeForge 采用现代网络技术构建，以确保最佳性能和安全性：',
            '• **客户端处理**：所有二维码生成都在您的浏览器中使用先进的 JavaScript 库完成',
            '• **React 和 TypeScript**：使用强大的类型安全前端技术构建，确保可靠性',
            '• **响应式设计**：针对桌面、平板和移动设备进行优化',
            '• **渐进式网络应用**：快速加载和离线功能，改善用户体验',
            '• **国际标准**：完全符合 ISO/IEC 18004 二维码标准'
          ]
        },
        future: {
          title: 'QRCodeForge 的未来',
          content: '我们不断创新和扩展我们的平台。即将推出的功能包括批量二维码生成、高级分析、API 访问和增强的自定义选项。我们的目标是保持最全面和用户友好的二维码解决方案。'
        }
      }
    }
  };

  const currentContent = content[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SharedHeader />

      {/* 动态背景元素 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -inset-10 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>
      </div>

      <div className="relative">
        {/* Header */}
        <header className="pt-20 pb-12">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl">
                <QrCode className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {currentContent.title}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {currentContent.subtitle}
            </p>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-6xl mx-auto px-4 pb-16">
          {/* Mission */}
          <section className="mb-16">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 md:p-12">
              <h2 className="text-3xl font-bold text-white mb-6 text-center">
                {currentContent.sections.mission.title}
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed text-center max-w-4xl mx-auto">
                {currentContent.sections.mission.content}
              </p>
            </div>
          </section>

          {/* Features */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              {currentContent.sections.features.title}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentContent.sections.features.items.map((item, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-white/10 rounded-lg mr-3 text-blue-300">
                      {item.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-white">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Story */}
          <section className="mb-16">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
              <h2 className="text-2xl font-semibold text-white mb-4">
                {currentContent.sections.story.title}
              </h2>
              <p className="text-gray-300 leading-relaxed">
                {currentContent.sections.story.content}
              </p>
            </div>
          </section>

          {/* Commitment */}
          <section className="mb-16">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
              <h2 className="text-2xl font-semibold text-white mb-6">
                {currentContent.sections.commitment.title}
              </h2>
              <div className="space-y-3">
                {currentContent.sections.commitment.content.map((item, index) => (
                  <p
                    key={index}
                    className="text-gray-300 leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: item
                        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
                        .replace(/• /g, '• ')
                    }}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Technology */}
          <section className="mb-16">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
              <h2 className="text-2xl font-semibold text-white mb-6">
                {currentContent.sections.technology.title}
              </h2>
              <div className="space-y-3">
                {currentContent.sections.technology.content.map((item, index) => (
                  <p
                    key={index}
                    className="text-gray-300 leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: item
                        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
                        .replace(/• /g, '• ')
                    }}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Future */}
          <section>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
              <h2 className="text-2xl font-semibold text-white mb-4">
                {currentContent.sections.future.title}
              </h2>
              <p className="text-gray-300 leading-relaxed">
                {currentContent.sections.future.content}
              </p>
            </div>
          </section>
        </div>
        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default About;